// SUB-OFFICE 7 — THE PUBLIC RECORD. The gallery reader (strangers' guard-
// passed redacted offerings, WITNESS stamps — the only unsanctioned kindness
// in the building), the Wall of the Compliant, the Registry of the Purged,
// the Ledger of Clemencies, and the Rotunda's Daily Directive board with its
// podium of three and the personal percentile line.

import { api } from '../net';
import { CompositionMeter } from '../telemetry';
import { holdSlamButton, printLayer, redacted } from './chrome';
import type { EventBus } from '../entities/entities';
import type { DailyResponse, LedgerEntry, Verdict } from '../../shared/types';

type View = 'ledger' | 'compliant' | 'purged' | 'clemency';

export function openRecordReader(bus: EventBus, initial: View = 'ledger'): Promise<void> {
  return new Promise((resolve) => {
    const layer = printLayer();
    const card = document.createElement('div');
    card.className = 'paper-modal wide';
    card.innerHTML =
      `<div class="paper-head letterhead"><span>THE PUBLIC RECORD</span><span>SUB-OFFICE 7</span></div>`;
    const tabs = document.createElement('div');
    tabs.style.cssText = 'display:flex;gap:6px;padding:8px 18px 0;flex-wrap:wrap';
    card.appendChild(tabs);
    const body = document.createElement('div');
    body.className = 'paper-body typewriter';
    card.appendChild(body);
    const foot = document.createElement('div');
    foot.className = 'paper-foot';
    card.appendChild(foot);
    layer.appendChild(card);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'slam-btn secondary';
    closeBtn.textContent = 'STEP BACK FROM THE GLASS';
    closeBtn.addEventListener('click', () => {
      card.remove();
      resolve();
    });
    foot.appendChild(closeBtn);

    const TABS: Array<{ v: View; label: string }> = [
      { v: 'ledger', label: 'THE GALLERY' },
      { v: 'compliant', label: 'WALL OF THE COMPLIANT' },
      { v: 'purged', label: 'REGISTRY OF THE PURGED' },
      { v: 'clemency', label: 'LEDGER OF CLEMENCIES' },
    ];
    for (const t of TABS) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'slam-btn secondary';
      b.style.cssText = 'font-size:9px;padding:6px 9px;box-shadow:none';
      b.textContent = t.label;
      b.addEventListener('click', () => void show(t.v, 0));
      tabs.appendChild(b);
    }

    const show = async (view: View, cursor: number): Promise<void> => {
      body.innerHTML = '<div class="compose-note">THE APPARATUS RETRIEVES…</div>';
      const res = await api.record(view, cursor);
      body.innerHTML = '';
      if (!res.ok || !res.data) {
        body.textContent = res.memo || 'RECORDS ROOM TEMPORARILY SEALED.';
        return;
      }
      const d = res.data;
      if (d.header) {
        const h = document.createElement('div');
        h.className = 'compose-note';
        h.textContent = d.header;
        body.appendChild(h);
      }
      if (view === 'ledger') {
        for (const raw of d.entries) {
          const e = raw as unknown as LedgerEntry;
          body.appendChild(galleryFrame(bus, e));
        }
      } else if (view === 'compliant') {
        (d.entries as Array<{ designation: string; standing: number; grade: string }>).forEach((e, i) => {
          const row = document.createElement('div');
          row.style.cssText =
            'display:flex;justify-content:space-between;font-size:12px;padding:3px 0;border-bottom:1px dotted rgba(28,26,23,.3)' +
            (e.grade === 'BELOVED OF THE BUREAU' ? ';color:var(--gold)' : '');
          row.innerHTML =
            `<span class="tab-num">${cursor * 25 + i + 1}. ${esc(e.designation)}</span>` +
            `<span class="tab-num">${e.standing} — ${esc(e.grade)}</span>`;
          body.appendChild(row);
        });
      } else if (view === 'purged') {
        for (const raw of d.entries) {
          const e = raw as { id?: number; designation: string; final_words: string | null; epitaph: string | null };
          const row = document.createElement('div');
          row.style.cssText = 'border:1px solid rgba(28,26,23,.4);padding:7px 10px;margin-top:7px;font-size:12px';
          row.innerHTML =
            `<div class="field-label" style="text-decoration:line-through">${esc(e.designation)}</div>` +
            (e.final_words ? `<div>“${esc(e.final_words)}”</div>` : '') +
            (e.epitaph ? `<div style="opacity:.75;margin-top:2px">${esc(e.epitaph)}</div>` : '');
          // The public obituary permalink — /o/{purged.id}, opened in a new tab.
          // Rendered only when the row carries its numeric id (the private
          // petitioner UUID is never used here, per contract §23.3).
          if (e.id != null) {
            const link = document.createElement('a');
            link.href = `/o/${encodeURIComponent(String(e.id))}`;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.className = 'tab-num';
            link.style.cssText = 'display:inline-block;margin-top:6px;font-size:10px;color:var(--gold);text-decoration:none';
            link.textContent = 'THE OBITUARY IS PUBLIC →';
            row.appendChild(link);
          }
          body.appendChild(row);
        }
      } else {
        for (const raw of d.entries) {
          const e = raw as { designation: string; excerpt: string; memo?: string };
          const row = document.createElement('div');
          row.style.cssText = 'border:1px solid var(--clemency);padding:7px 10px;margin-top:7px;font-size:12px;color:var(--clemency)';
          const head = document.createElement('div');
          head.className = 'field-label';
          head.textContent = e.designation;
          row.appendChild(head);
          const ex = document.createElement('div');
          ex.appendChild(redacted(e.excerpt));
          row.appendChild(ex);
          body.appendChild(row);
        }
      }
      if (d.cursor !== null && d.cursor !== undefined) {
        const more = document.createElement('button');
        more.type = 'button';
        more.className = 'slam-btn secondary';
        more.style.cssText = 'margin-top:10px;font-size:10px;padding:8px 12px';
        more.textContent = 'FURTHER INTO THE RECORD';
        more.addEventListener('click', () => void show(view, d.cursor as number));
        body.appendChild(more);
      }
    };
    void show(initial, 0);
  });
}

function galleryFrame(bus: EventBus, e: LedgerEntry): HTMLElement {
  const frame = document.createElement('div');
  frame.style.cssText = 'border:2px solid var(--carbon);padding:8px 11px;margin-top:8px;background:var(--paper)';
  const head = document.createElement('div');
  head.className = 'field-label';
  head.textContent = `${e.designation}${e.seeded ? ' — RECOVERED, SUB-OFFICE 4 (CLOSED)' : ''}`;
  frame.appendChild(head);
  const ex = document.createElement('div');
  ex.style.cssText = 'font-size:12.5px;line-height:1.6';
  ex.appendChild(redacted(e.excerpt));
  frame.appendChild(ex);
  const meta = document.createElement('div');
  meta.style.cssText = 'display:flex;justify-content:space-between;align-items:center;margin-top:6px;gap:8px;flex-wrap:wrap';
  const stamp = document.createElement('span');
  stamp.className = `stamp ${/DENIED/.test(e.stamp) ? 'stamp-red' : 'stamp-ink'}`;
  stamp.style.cssText = 'font-size:10px;padding:2px 7px';
  stamp.textContent = e.stamp;
  meta.appendChild(stamp);
  const acts = document.createElement('div');
  acts.style.cssText = 'display:flex;gap:6px;align-items:center';
  const count = document.createElement('span');
  count.className = 'tab-num';
  count.style.fontSize = '11px';
  count.textContent = `WITNESSED ×${e.witnessed}`;
  acts.appendChild(count);
  acts.appendChild(
    holdSlamButton('WITNESS', () => {
      void (async () => {
        const res = await api.witness(e.id);
        if (res.ok && res.data) {
          count.textContent = `WITNESSED ×${res.data.witnessed}`;
          bus.emit('toast', res.data.already
            ? 'You have already witnessed this entry. Once is the entire ration.'
            : res.data.memo || 'The entry has been witnessed. This kindness is unsanctioned and now permanent.');
        } else {
          bus.emit('toast', res.memo || 'The witness stamp declined to descend.');
        }
      })();
    }, { className: 'secondary' })
  );
  const share = document.createElement('button');
  share.type = 'button';
  share.className = 'slam-btn secondary';
  share.style.cssText = 'font-size:9px;padding:6px 9px';
  share.textContent = 'PERMALINK';
  share.addEventListener('click', () => {
    const url = `${location.origin}/r/${encodeURIComponent(e.id)}`;
    const nav = navigator as Navigator & { share?: (d: { url: string; title?: string }) => Promise<void> };
    if (nav.share) {
      void nav.share({ url, title: `${e.stamp} — ${e.designation}` }).catch(() => undefined);
    } else {
      void navigator.clipboard?.writeText(url).then(
        () => bus.emit('toast', 'The permalink is on your clipboard. Distribution is your affair.'),
        () => bus.emit('toast', url)
      );
    }
  });
  acts.appendChild(share);
  meta.appendChild(acts);
  frame.appendChild(meta);
  return frame;
}

// ---------------------------------------------------------------------------
// THE DAILY DIRECTIVE — one planet-wide demand per UTC day
// ---------------------------------------------------------------------------

export function openDailyBoard(
  bus: EventBus,
  onVerdict: (v: Verdict) => Promise<void>
): Promise<void> {
  return new Promise((resolve) => {
    void (async () => {
      const layer = printLayer();
      const card = document.createElement('div');
      card.className = 'paper-modal wide letterhead-doc';
      card.innerHTML =
        `<div class="paper-head letterhead"><span>BUREAU-WIDE DIRECTIVE</span><span>COMPLIANCE IS UNIVERSAL</span></div>`;
      const body = document.createElement('div');
      body.className = 'paper-body typewriter';
      body.innerHTML = '<div class="compose-note">THE LETTERHEAD CASCADES…</div>';
      card.appendChild(body);
      const foot = document.createElement('div');
      foot.className = 'paper-foot';
      card.appendChild(foot);
      layer.appendChild(card);
      const close = (): void => {
        card.remove();
        resolve();
      };
      const closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'slam-btn secondary';
      closeBtn.textContent = 'STEP AWAY FROM THE BOARD';
      closeBtn.addEventListener('click', close);
      foot.appendChild(closeBtn);

      const res = await api.daily();
      if (!res.ok || !res.data) {
        body.innerHTML = '';
        body.textContent = res.memo || 'THE DIRECTIVE IS BEING TYPESET. MIDNIGHT IS WHEN THE BUREAU SAYS IT IS.';
        return;
      }
      const d = res.data;
      renderBoard(body, d);

      if (!d.mine) {
        const area = document.createElement('textarea');
        area.className = 'compose-area';
        area.style.cssText = 'width:100%;margin:8px 0 0;min-height:90px';
        area.maxLength = 1200;
        area.spellcheck = false;
        body.appendChild(area);
        const meter = new CompositionMeter();
        meter.attach(area);
        foot.appendChild(
          holdSlamButton('ANSWER THE DIRECTIVE', () => {
            const text = area.value.trim();
            if (!text) {
              bus.emit('toast', 'The planet answered with words. Yours are absent.');
              return;
            }
            void (async () => {
              const telemetry = meter.collect();
              meter.detach();
              const sub = await api.dailySubmit(text, telemetry);
              if (!sub.ok || !sub.data) {
                bus.emit('toast', sub.memo || 'The directive declined the answer. Today remains today.');
                return;
              }
              card.remove();
              await onVerdict(sub.data.verdict);
              bus.emit('toast', sub.data.percentile_line);
              resolve();
            })();
          })
        );
      }
    })();
  });
}

function renderBoard(body: HTMLElement, d: DailyResponse): void {
  body.innerHTML =
    `<div class="lh-sub letterhead">${esc(d.date)} — THEME: ${esc(d.theme).toUpperCase()} — ANSWERED ${d.answered} TIMES TODAY</div>` +
    `<hr class="rule"><div style="white-space:pre-wrap">${esc(d.demand)}</div>`;
  if (d.podium && d.podium.length) {
    const lbl = document.createElement('div');
    lbl.className = 'field-label';
    lbl.style.marginTop = '10px';
    lbl.textContent = 'LEAST DISAPPOINTING (TODAY) — UNDER GLASS';
    body.appendChild(lbl);
    for (const p of d.podium) {
      const row = document.createElement('div');
      row.style.cssText = 'font-size:12px;margin-top:4px;border-left:3px solid var(--gold);padding-left:8px';
      const head = document.createElement('div');
      head.className = 'field-label';
      head.textContent = `${p.rank}. ${p.designation}`;
      row.appendChild(head);
      const ex = document.createElement('div');
      ex.appendChild(redacted(p.excerpt));
      row.appendChild(ex);
      body.appendChild(row);
    }
  }
  if (d.mine) {
    const mine = document.createElement('div');
    mine.style.cssText = 'margin-top:10px;border:1px dashed var(--carbon);padding:7px 10px;font-size:12px';
    mine.innerHTML =
      `<div class="field-label">YOUR ANSWER IS ON FILE</div>` +
      `<div>${esc(d.mine.memo || '')}</div>` +
      `<div class="tab-num" style="margin-top:3px">${esc(d.mine.percentile_line)}</div>`;
    body.appendChild(mine);
  }
}

function esc(s: string): string {
  const div = document.createElement('div');
  div.textContent = s ?? '';
  return div.innerHTML;
}
